"use client";

// WO-GF-001 B4.1 — map hydrator. Document-scoped, same family as MarketplaceCartLayer: it finds the
// dead photocopied map container in the mirrored body (the captured `.mapDefault`/gm-style snapshot with
// JS stripped) and replaces it in place with the real <PortMap> — turning an R6-6 "visually cloned but
// functionally dead" region into live machinery, without re-extracting the page body. The surrounding
// mirror stays byte-identical; the map region itself is masked in the gate (dynamic + key-gated).

import { useEffect } from "react";
import { createRoot, type Root } from "react-dom/client";
import PortMap, { type PortMapMarker } from "./PortMap";

// Deterministic map-region height. MUST match tooling/port-harness/config.mjs NORMALIZE_HEIGHTS for
// `.mapDefault` (320) — the harness forces this exact height on BOTH live + build captures before the
// pixel diff, so the region can never shift content below it. Pinning the runtime to the same value
// keeps real UX == the gated layout (the async map's own height is non-deterministic; we don't trust it).
const MAP_REGION_HEIGHT = 320;

export type MapLayerProps = {
  markers: PortMapMarker[];
  /** container selector inside the mirror body to upgrade. Legacy default map = ".mapDefault". */
  selector?: string;
  mode?: "default" | "mountain";
  surface?: string;
};

export default function MapLayer({ markers, selector = ".mapDefault", mode = "default", surface }: MapLayerProps) {
  useEffect(() => {
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) return;

    // ZERO layout shift (gate-critical): pin the map region to the deterministic normalised height. The
    // harness forces the same height on live + build captures before diffing, so the async map can never
    // shift content below it, and the runtime layout equals the gated layout. The region is masked.
    container.setAttribute("data-map-hydrated", "true");
    container.style.height = `${MAP_REGION_HEIGHT}px`;
    container.style.minHeight = `${MAP_REGION_HEIGHT}px`;
    container.style.maxHeight = `${MAP_REGION_HEIGHT}px`;
    container.innerHTML = "";
    const host = document.createElement("div");
    host.style.width = "100%";
    host.style.height = "100%";
    container.appendChild(host);

    let root: Root | null = createRoot(host);
    root.render(<PortMap markers={markers} mode={mode} surface={surface} />);

    return () => {
      const r = root;
      root = null;
      // Defer unmount out of the render cycle (React warns on sync unmount during commit).
      queueMicrotask(() => r?.unmount());
    };
  }, [markers, selector, mode, surface]);

  return null;
}
